import { useCallback, useEffect, useRef, useState } from "react";
import type { NormalizedMenu } from "../lib/menuTypes";

type MenuState = {
  menu?: NormalizedMenu;
  status: "loading" | "ready" | "error";
  error?: string;
};

const MENU_REFRESH_MS = 60 * 1000;

export function useMenu(autoRefresh: boolean) {
  const [state, setState] = useState<MenuState>({ status: "loading" });
  const menuRef = useRef<NormalizedMenu | undefined>(undefined);

  const loadMenu = useCallback(async () => {
    const hasExistingMenu = Boolean(menuRef.current);
    try {
      setState((current) => ({ ...current, status: current.menu ? "ready" : "loading", error: undefined }));
      const response = await fetch("/api/menu", { headers: { Accept: "application/json" } });
      if (!response.ok) throw new Error("Live menu request failed");
      const menu = (await response.json()) as NormalizedMenu;
      menuRef.current = menu;
      setState({ status: "ready", menu });
    } catch (apiError) {
      if (hasExistingMenu) {
        setState((current) => ({
          ...current,
          status: "ready",
          error: apiError instanceof Error ? apiError.message : "Unable to refresh menu"
        }));
        return;
      }

      try {
        const fallbackResponse = await fetch("/data/menu-fallback.json", { headers: { Accept: "application/json" } });
        if (!fallbackResponse.ok) throw apiError;
        const fallback = (await fallbackResponse.json()) as NormalizedMenu;
        const fallbackMenu = { ...fallback, source: "fallback" as const };
        menuRef.current = fallbackMenu;
        setState({ status: "ready", menu: fallbackMenu });
      } catch {
        setState({
          status: "error",
          error: apiError instanceof Error ? apiError.message : "Unable to load menu"
        });
      }
    }
  }, []);

  useEffect(() => {
    void loadMenu();
  }, [loadMenu]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      void loadMenu();
    }, MENU_REFRESH_MS);
    return () => window.clearInterval(interval);
  }, [loadMenu]);

  return {
    ...state,
    refresh: loadMenu
  };
}
